import { useEffect } from "react";
import { createContext, useState } from "react";
import { fetchConfig } from "../components/global/commonNetworkController";
import { AADHAR_REQUEST_COUNT, BILLNUMBERNAME, CGST, CHECKIN_TIME_DEFINE, CORPORATE_MEAL, INVOICE_COUNT_ENABLE, SGST } from "../constants/ConfigConstant";
import { isEmpty, printDebugLog } from "../constants/Constants";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Navigate } from "react-router-dom";

const AuthContext = createContext({});

const DIALOG_ACTION = {
  postive: "", negative: "",
  positiveAction: () => { }, negativeAction: () => { },
}

export const AuthProvider = ({ children }) => {
  const [activeShopHash, setActiveShopHash] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);


  const [checkInTimeConfig, setCheckInTime] = useState("");
  const [cgstConfig, setCgst] = useState(0.0);
  const [sgstConfig, setSgst] = useState(0.0);
  const [configCorporateMeal, setConfigCorporateMeal] = useState(new Map());
  const [prefixBillName, setPrefixBillName] = useState("");
  const [isInvoiceAllowed, setIsInvoiceAllowed] = useState(false);

  const [aadharVerificationEnabled, setAadharVerificationEnabled] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMsg, setDialogMsg] = useState("");
  const [dialogAction, setDialogAction] = useState(DIALOG_ACTION);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("authUser");
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setAuthUser(userData);
      setActiveShopHash(userData.activeShopHash);
      setIsLoggedIn(true);
    }
    setIsLoading(false);
  }, []);


  const showConfirmDialog = ({ title, message, positive = "", positiveAction = null, negative = "", negativeAction = null }) => {
    setDialogTitle(title)
    setDialogMsg(message);
    setOpenDialog(true);
    setDialogAction(prev => ({
      ...prev,
      postive: positive,
      positiveAction: () => {
        if (positiveAction) positiveAction();
        handleClose();
      },
      negative: negative,
      negativeAction: () => {
        if (negativeAction) negativeAction();
        handleClose();
      }
    }))
  }

  const showMessageDialog = (message) => {
    showConfirmDialog({ title: "Access Denied", message: message, positive: "Ok" });
  }

  const isRoleAllowed = (role) => {
    const roles = (authUser?.roles ?? []);
    if (roles.length === 0) {
      return <Navigate replace="/login" />;
    }
    if (!roles[role]) {
      showMessageDialog(`You do not have permission for role: ${role}`);
      return false;
    }
    return true;
  }

  const isFeatureAllowed = (feature) => {
    const packages = (authUser?.packages ?? []);
    if (packages.length === 0) {
      return <Navigate replace="/login" />;
    }
    if (!packages[feature]) {
      showMessageDialog(`You do not have permission for feature: ${feature}`);
      return false;
    }
    return true;
  }

  const handleClose = () => {
    setOpenDialog(false);
    setDialogAction(DIALOG_ACTION);
    setDialogMsg("");
  };


  useEffect(() => {
    printDebugLog("useEffect", authUser);
    if (authUser && authUser?.activeShopHash) {
      getConfig(CGST);
      getConfig(SGST);
      getConfig(CHECKIN_TIME_DEFINE);
      getConfig(CORPORATE_MEAL);
      getConfig(BILLNUMBERNAME);
      getConfig(INVOICE_COUNT_ENABLE);
      getConfig(AADHAR_REQUEST_COUNT);
    }
  }, [authUser]);

  const loginData = (userData) => {
    setAuthUser(userData);
    setActiveShopHash(userData.activeShopHash);
    setIsLoading(false);
    setIsLoggedIn(true);
    //sessionStorage.setItem("authUser", JSON.stringify(userData));
  };

  const logout = () => {
    sessionStorage.removeItem("authUser");
    sessionStorage.removeItem('isLoggedIn');
    setAuthUser(null);
    // setIsLoading(false);
    setIsLoggedIn(false);
  };


  const updateConfiguration = (configName, value) => {
    if (isEmpty(configName)) {
      return;
    }
    if (configName === CGST) {
      if (value) {
        setCgst(value);
      }
    } else if (configName === SGST) {
      if (value) {
        setSgst(value);
      }
    } else if (configName === CHECKIN_TIME_DEFINE) {
      if (value) {
        setCheckInTime(value);
      }
    } else if (configName === CORPORATE_MEAL) {
      if (value) {
        setConfigCorporateMeal(new Map(Object.entries(value)));
      }
    } else if (configName === BILLNUMBERNAME) {
      if (value) {
        setPrefixBillName(value);
      }
    } else if (configName === INVOICE_COUNT_ENABLE) {
      if (!isEmpty(value)) {
        setIsInvoiceAllowed(Boolean(value));
      }
    } else if (configName === AADHAR_REQUEST_COUNT) {
      if (!isEmpty(value)) {
        setAadharVerificationEnabled(Boolean(value));
      }
    }
  }

  const getConfig = async (mConfigName) => {
    await fetchConfig({
      authUser: authUser,
      activeShopHash: authUser.activeShopHash,
      configName: mConfigName,
      onResponseCallback: (configName, value) => {
        updateConfiguration(configName, value);
      }
    })
  }

  //console.log("user", authUser);

  return (
    <AuthContext.Provider value={{
      isLoggedIn, authUser, activeShopHash, isLoading, loginData, logout,
      cgstConfig, sgstConfig, checkInTimeConfig, configCorporateMeal,
      isInvoiceAllowed, prefixBillName, aadharVerificationEnabled,
      updateConfiguration, isRoleAllowed, isFeatureAllowed, showMessageDialog, showConfirmDialog
    }}>
      {children}
      {/* Role warning dialog */}
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>{dialogMsg}</DialogContent>
        <DialogActions>
          {
            !isEmpty(dialogAction.postive)
            && <Button onPointerDown={() => dialogAction.positiveAction()}>{dialogAction.postive}</Button>
          }
          {
            !isEmpty(dialogAction.negative)
            && <Button onPointerDown={() => dialogAction.negativeAction()}>{dialogAction.negative}</Button>
          }
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};

export default AuthContext;
